import { Collapsible, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { ChevronDown, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import * as posthog from "../../../analytics/safePosthog";
import { AnalyticsEvents } from "../../../analytics/events";
import { serviceItems, subContainerVariants, subItemVariants } from "./constants";

const MotionDiv = motion.div;

interface ServicesCollapsibleProps {
  isActive: boolean;
  onNavigate: (to: string) => void;
}

export function ServicesCollapsible({ isActive, onNavigate }: ServicesCollapsibleProps) {
  const [open, setOpen] = useState(isActive);
  const location = useLocation();

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
        if (e.open) {
          posthog.capture(AnalyticsEvents.SERVICES_MENU_OPENED, { nav_type: "drawer" });
        }
      }}
    >
      <Collapsible.Trigger
        w="full"
        display="flex"
        flexDir="row"
        justifyContent="space-between"
        alignItems="center"
        px="0.95rem"
        py="0.9rem"
        rounded="0.85rem"
        bg={isActive ? "rgba(34,197,94,0.08)" : "transparent"}
        _hover={{
          bg: isActive ? "rgba(34,197,94,0.10)" : "rgba(34,197,94,0.06)",
        }}
        transition="all 0.18s ease"
      >
        <HStack gap="0.75rem">
          <Flex
            w="1.55rem"
            h="1.55rem"
            align="center"
            justify="center"
            flexShrink={0}
            color={isActive ? "primary.surface.default" : "greyscale.text.body"}
          >
            <Sparkles size={18} />
          </Flex>
          <Text
            textStyle={isActive ? "bodyRegular.strong" : "bodyRegular.base"}
            color={isActive ? "greyscale.text.title" : "greyscale.text.body"}
          >
            Our Services
          </Text>
        </HStack>

        <Collapsible.Indicator
          transition="transform 0.2s"
          _open={{ transform: "rotate(180deg)" }}
          color={isActive ? "greyscale.text.title" : "greyscale.text.body"}
        >
          <ChevronDown size={16} />
        </Collapsible.Indicator>
      </Collapsible.Trigger>

      <Collapsible.Content mt="0.4rem">
        <AnimatePresence initial={false}>
          {open && (
            <MotionDiv
              key="services-sub"
              variants={subContainerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <VStack
                align="stretch"
                gap="0.25rem"
                p="0.45rem"
                rounded="0.95rem"
                bg="rgba(34,197,94,0.045)"
                border="1px solid rgba(34,197,94,0.08)"
              >
                {serviceItems.map((item) => {
                  const isSubActive = isActive && location.pathname === item.path;
                  return (
                    <MotionDiv key={item.path} variants={subItemVariants}>
                      <Flex
                        px="0.9rem"
                        py="0.72rem"
                        rounded="0.7rem"
                        gap="0.75rem"
                        alignItems="center"
                        cursor="pointer"
                        color={isSubActive ? "primary.surface.default" : "greyscale.text.body"}
                        bg={isSubActive ? "white" : "transparent"}
                        border="1px solid"
                        borderColor={isSubActive ? "rgba(34,197,94,0.18)" : "transparent"}
                        boxShadow={isSubActive ? "0 6px 16px rgba(15,23,42,0.06)" : "none"}
                        transition="all 0.18s ease"
                        _hover={{
                          bg: "white",
                          color: "primary.surface.default",
                          borderColor: "rgba(34,197,94,0.16)",
                          boxShadow: "0 6px 14px rgba(15,23,42,0.04)",
                          transform: "translateX(2px)",
                        }}
                        onClick={() => {
                          posthog.capture(AnalyticsEvents.SERVICE_NAV_CLICKED, {
                            service_name: item.label,
                            nav_type: "drawer",
                          });
                          onNavigate(item.path);
                        }}
                      >
                        <Flex
                          w="1.45rem"
                          h="1.45rem"
                          align="center"
                          justify="center"
                          flexShrink={0}
                          color="currentColor"
                        >
                          <item.icon size={15} />
                        </Flex>
                        <Text fontSize="0.875rem" fontWeight="500">
                          {item.label}
                        </Text>
                      </Flex>
                    </MotionDiv>
                  );
                })}
              </VStack>
            </MotionDiv>
          )}
        </AnimatePresence>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
